import { create } from 'zustand';
import { getInvoices, getInvoice, addInvoice, moveInvoice as moveInvoiceService, addBulkInvoices, deleteInvoice as deleteInvoiceService } from '../services/invoice.service';
import { makeRequest } from '../services/https';
import { Invoice, AddInvoice, InvoiceEnteredResponse } from '../types/invoice.type';
import { generatePresignedUrl, uploadFileToPresignedUrl } from '../services/upload.service';
import { FileType, FileExtension, getFileExtension } from '../types/upload.type';
import { formatLocalDateKey } from '../utils/format';
import { buildFolderBreadcrumb } from '../utils/folder.utils';

import { INVOICE_PAGE_SIZE } from '../pages/dashboard/invoiceTheme';

const INVOICE_STALE_MS = 60_000; // 60 seconds

interface FileWithName {
  file: File;
  name: string;
  id: string;
  notes?: string;
}

interface InvoiceState {
  invoices: Invoice[];
  currentFolderId: string | undefined;
  currentFolderPath: Invoice[];
  allFolders: Invoice[];
  searchResults: Invoice[] | null;
  isLoading: boolean;
  isContentLoading: boolean;
  isSearching: boolean;
  error: string | null;
  // Pagination
  currentPage: number;
  hasMore: boolean;
  totalCount: number;
  // null = no entered filter. true/false sent as GET /invoice?entered=
  enteredFilter: boolean | null;
  // Cache tracking
  _lastFetchedOrgId: string | null;
  _lastFetchedFolderId: string | undefined;
  _lastFetchedAt: number | null;
  _lastFetchedEntered: boolean | null;
  _lastSearch: {
    organizationId: string;
    query: string;
    fromDate?: Date;
    toDate?: Date;
  } | null;

  // Actions
  fetchInvoices: (organizationId: string, params?: any) => Promise<void>;
  fetchInvoice: (invoiceId: string) => Promise<void>;
  addInvoice: (data: AddInvoice) => Promise<void>;
  loadFolder: (organizationId: string, folderId?: string, page?: number) => Promise<void>;
  loadAllFolders: (organizationId: string) => Promise<void>;
  searchInvoices: (organizationId: string, query: string, fromDate?: Date, toDate?: Date) => Promise<void>;
  clearSearch: () => void;
  moveInvoice: (invoiceId: string, targetFolderId: string) => Promise<void>;
  addBulkInvoices: (files: FileWithName[], organizationId: string, parentId?: string) => Promise<void>;
  renameInvoice: (invoiceId: string, newName: string) => Promise<void>;
  deleteInvoice: (invoiceId: string) => Promise<void>;
  updateInvoiceNotes: (invoiceId: string, notes: string, existingData?: any) => Promise<void>;
  setEnteredFilter: (value: boolean | null) => void;
  applyInvoiceEntered: (update: InvoiceEnteredResponse) => void;
}

export const useInvoiceStore = create<InvoiceState>((set, get) => ({
  // Initial state
  invoices: [],
  currentFolderId: undefined,
  currentFolderPath: [],
  allFolders: [],
  searchResults: null,
  isLoading: false,
  isContentLoading: false,
  isSearching: false,
  error: null,
  currentPage: 1,
  hasMore: false,
  totalCount: 0,
  enteredFilter: null,
  _lastFetchedOrgId: null,
  _lastFetchedFolderId: undefined,
  _lastFetchedAt: null,
  _lastFetchedEntered: null,
  _lastSearch: null,

  // Load initial invoices
  fetchInvoices: async (organizationId: string, params?: any) => {
    set({ isLoading: true, error: null });
    try {
      const response = await getInvoices(organizationId, params);
      if (response && response.result_list) {
        set({ invoices: response.result_list, isLoading: false, error: null });
      } else {
        set({ invoices: [], isLoading: false, error: null });
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Error fetching invoices';
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  fetchInvoice: async (invoiceId: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await getInvoice(invoiceId);
      set({ isLoading: false, error: null });
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Error fetching invoice';
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  addInvoice: async (data: AddInvoice) => {
    set({ isLoading: true, error: null });
    try {
      await addInvoice(data);
      set({ isLoading: false, error: null, _lastFetchedAt: null });
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Error adding invoice';
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  // Load folder contents
  loadFolder: async (organizationId: string, folderId?: string, page = 1) => {
    const s = get();
    const enteredFilter = s.enteredFilter;
    // Skip fetch only when re-requesting the same page that is already loaded and fresh
    if (
      page === s.currentPage &&
      s._lastFetchedOrgId === organizationId &&
      s._lastFetchedFolderId === folderId &&
      s._lastFetchedEntered === enteredFilter &&
      s._lastFetchedAt !== null &&
      Date.now() - s._lastFetchedAt < INVOICE_STALE_MS &&
      s.invoices.length > 0
    ) {
      return;
    }

    set({ isContentLoading: true, error: null, currentFolderId: folderId });
    try {
      const limit = INVOICE_PAGE_SIZE;
      const response = await getInvoices(organizationId, { 
        parent_id: folderId,
        page,
        limit,
        ...(enteredFilter !== null ? { entered: enteredFilter } : {}),
      });
      if (response && response.result_list) {
        const currentFolderPath = folderId
          ? buildFolderBreadcrumb(folderId, get().allFolders)
          : [];
        
        const totalCount = response.total_count || 0;
        const hasMore = (page * limit) < totalCount;
        
        set({ 
          invoices: response.result_list, 
          isContentLoading: false, 
          error: null,
          currentFolderPath,
          currentPage: page,
          hasMore,
          totalCount,
          _lastFetchedOrgId: organizationId,
          _lastFetchedFolderId: folderId,
          _lastFetchedAt: Date.now(),
          _lastFetchedEntered: enteredFilter,
        });
      } else {
        set({ 
          invoices: page === 1 ? [] : get().invoices, 
          isContentLoading: false, 
          error: null,
          currentFolderPath: [],
          currentPage: page,
          hasMore: false,
          totalCount: 0,
          _lastFetchedOrgId: organizationId,
          _lastFetchedFolderId: folderId,
          _lastFetchedAt: Date.now(),
          _lastFetchedEntered: enteredFilter,
        });
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Error loading folder';
      set({ error: errorMessage, isContentLoading: false });
      throw error;
    }
  },

  // Load all folders
  loadAllFolders: async (organizationId: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await getInvoices(organizationId, { type: 'folder' });
      if (response && response.result_list) {
        set({ allFolders: response.result_list, isLoading: false, error: null });
      } else {
        set({ allFolders: [], isLoading: false, error: null });
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Error loading folders';
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  // Search invoices
  searchInvoices: async (organizationId: string, query: string, fromDate?: Date, toDate?: Date) => {
    if (!query.trim() && !fromDate && !toDate) {
      set({ searchResults: null, isSearching: false });
      return;
    }

    set({ isSearching: true, error: null });
    try {
      const params: any = {
        parent_id: get().currentFolderId,
      };

      if (query.trim()) {
        params.search = query.trim();
      }
      if (fromDate) {
        params.from_date = formatLocalDateKey(fromDate);
      }
      if (toDate) {
        params.to_date = formatLocalDateKey(toDate);
      }
      const enteredFilter = get().enteredFilter;
      if (enteredFilter !== null) {
        params.entered = enteredFilter;
      }
      
      const response = await getInvoices(organizationId, params);
      if (response && response.result_list) {
        set({
          searchResults: response.result_list,
          isSearching: false,
          error: null,
          _lastSearch: { organizationId, query, fromDate, toDate },
        });
      } else {
        set({
          searchResults: [],
          isSearching: false,
          error: null,
          _lastSearch: { organizationId, query, fromDate, toDate },
        });
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Error searching invoices';
      set({ error: errorMessage, isSearching: false });
      throw error;
    }
  },

  // Clear search
  clearSearch: () => {
    set({ searchResults: null, isSearching: false, _lastSearch: null });
  },

  // Move invoice to different folder
  moveInvoice: async (invoiceId: string, targetFolderId: string) => {
    set({ isLoading: true, error: null });
    try {
      await moveInvoiceService(invoiceId, targetFolderId);
      set({ isLoading: false, error: null, _lastFetchedAt: null });
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Error moving invoice';
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  // Rename invoice/folder
  renameInvoice: async (invoiceId: string, newName: string) => {
    set({ isLoading: true, error: null });
    try {
      await makeRequest({
        url: `/invoice/${invoiceId}`,
        method: 'put',
        data: { data: { name: newName } },
      });
      set({ isLoading: false, error: null, _lastFetchedAt: null });
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Error renaming item';
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  // Delete invoice/folder
  deleteInvoice: async (invoiceId: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await deleteInvoiceService(invoiceId);
      set({ isLoading: false, error: null, _lastFetchedAt: null });
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Error deleting item';
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  // NEW: Bulk upload invoices
  addBulkInvoices: async (files: FileWithName[], organizationId: string, parentId?: string) => {
    set({ isLoading: true, error: null });
    try {
      const bulkData: AddInvoice[] = [];

      // Upload each file individually to get asset IDs
      for (const fileData of files) {
        try {
          // Validate file extension
          const fileExtension = getFileExtension(fileData.file.name);
          if (!fileExtension) {
            throw new Error(`Unsupported file type for ${fileData.name}. Supported types: ${Object.values(FileExtension).join(', ')}`);
          }

          // Step 1: Get presigned URL from backend
          const presignedUrlRequest = {
            original_name: fileData.file.name
          };
          
          const presignedUrlResponse = await generatePresignedUrl(
            presignedUrlRequest,
            FileType.CUSTOM,
            fileExtension
          );

          // Step 2: Upload file to presigned URL
          await uploadFileToPresignedUrl(fileData.file, presignedUrlResponse.upload_url);

          // Step 3: Create invoice data with custom name and notes
          bulkData.push({
            organization_id: organizationId,
            asset_id: presignedUrlResponse.id,
            filename: fileData.name, // Use custom name as filename
            parent_id: parentId || "",
            type: 'file' as const,
            data: {
              name: fileData.name, // Keep this for backward compatibility
              ...(fileData.notes && { notes: fileData.notes }) // Add notes if provided
            }
          });

          console.log(`Successfully uploaded ${fileData.name} with asset ID: ${presignedUrlResponse.id}`);
        } catch (error: any) {
          console.error(`Failed to upload ${fileData.name}:`, error);
          throw new Error(`Failed to upload ${fileData.name}: ${error.message || 'Unknown error'}`);
        }
      }

      // Send to bulk endpoint
      await addBulkInvoices(bulkData);
      
      // Reload current folder and all folders
      await get().loadFolder(organizationId, get().currentFolderId);
      await get().loadAllFolders(organizationId);
      set({ isLoading: false, error: null });
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Error uploading files';
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  // Update invoice notes
  updateInvoiceNotes: async (invoiceId: string, notes: string, existingData?: any) => {
    set({ isLoading: true, error: null });
    try {
      await makeRequest({
        url: `/invoice/${invoiceId}`,
        method: 'put',
        data: {
          data: {
            ...existingData,
            notes
          }
        },
      });
      set({ isLoading: false, error: null, _lastFetchedAt: null });
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Error updating notes';
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  setEnteredFilter: (value) => {
    set({ enteredFilter: value, _lastFetchedAt: null });
  },

  applyInvoiceEntered: (update) => {
    const merge = (item: Invoice): Invoice =>
      item.id === update.id
        ? {
            ...item,
            entered: update.entered,
            entered_at: update.entered_at,
            entered_by: update.entered_by,
          }
        : item;

    set((state) => {
      const filter = state.enteredFilter;
      const keep = (item: Invoice) => {
        if (filter === null || item.id !== update.id) return true;
        return Boolean(item.entered) === filter;
      };
      const nextInvoices = state.invoices.map(merge).filter(keep);
      const removedFromList = state.invoices.some((item) => item.id === update.id)
        && !nextInvoices.some((item) => item.id === update.id);
      return {
        invoices: nextInvoices,
        searchResults: state.searchResults
          ? state.searchResults.map(merge).filter(keep)
          : state.searchResults,
        totalCount: removedFromList ? Math.max(0, state.totalCount - 1) : state.totalCount,
      };
    });
  },
})); 